
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  Task, BoxStatusType, ProjectsPool, Lead, Attachment,
} from '@/types';

@Component
export default class TaskActivityBox extends Vue {
  @Prop(String) readonly value!: BoxStatusType | null;

  // HACK: task può essere undefined quando il componente è readonly
  @Prop(Object) readonly task: Task | undefined;

  @Prop(String) readonly name!: string;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;
  @Prop({ type: Boolean, default: false }) readonly loading!: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: String, default: 'success' }) readonly doneColor!: string;
  @Prop({ type: Boolean, default: false }) readonly doneDisabled!: boolean;
  @Prop({ type: Boolean, default: false }) readonly useFather!: boolean
  @Prop({ type: Boolean, default: false }) readonly attachmentsNotRequired!: boolean

  boxStatus: BoxStatusType = 'todo';

  updateTimeout: number | undefined;

  created() {
    console.log('TaskActivityBox: created()'); // eslint-disable-line no-console

    if (this.value === null) {
      this.boxStatus = this.attachmentsCount === 0 ? 'todo' : 'done';
    } else {
      this.boxStatus = this.value;
    }
  }

  @Watch('value')
  onValue(val: BoxStatusType) {
    console.log('TaskActivityBox: onValue()'); // eslint-disable-line no-console

    this.boxStatus = val;
  }

  @Watch('attachmentsCount')
  onAttachmentsCount(val: number, oldVal: number) {
    console.log(`${this.constructor.name}: onAttachmentsCount() ${oldVal} => ${val}`); // eslint-disable-line no-console

    // Se lo stato attuale è 'done'...
    if (this.boxStatus === 'done') {
      // ... se siamo passati da 1+ a 0 allegati...
      if (oldVal > 0 && val === 0) {
        // ... si prepara ad aggiornare da 'done' a 'todo'
        this.handleUpdate();
      } else

      // ... se invece siamo passati da 0 a 1+ allegati...
      if (oldVal === 0 && val > 0) {
        // ... ferma eventuali aggiornamenti da 'done' a 'todo'
        if (this.updateTimeout) clearTimeout(this.updateTimeout);
      }
    }
  }

  @Watch('doneDisabled')
  onDoneDisabled(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onDoneDisabled() ${oldVal} => ${val}`); // eslint-disable-line no-console

    // Se lo stato attuale è 'done'...
    if (this.boxStatus === 'done') {
      // ... se siamo passati da doneDisabled = false a doneDisabled = true...
      if (oldVal === false && val === true) {
        // ... si prepara ad aggiornare da 'done' a 'todo'
        this.handleUpdate();
      } else

      // ... se invece siamo passati da doneDisabled = true a doneDisabled = false...
      if (oldVal === true && val === false) {
        // ... ferma eventuali aggiornamenti da 'done' a 'todo'
        if (this.updateTimeout) clearTimeout(this.updateTimeout);
      }
    }
  }

  handleUpdate() {
    console.log(`${this.constructor.name}: handleUpdate()`); // eslint-disable-line no-console

    if (this.updateTimeout) clearTimeout(this.updateTimeout);

    this.updateTimeout = setTimeout(() => {
      console.log(`${this.constructor.name}: handleUpdate() timeout!`); // eslint-disable-line no-console

      this.boxStatus = 'todo';
      this.$emit('update', 'todo');
    }, 0); // Debouncing disattivato
  }

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get childLead(): Lead | null {
    return this.task
      && this.task.relationship === 'father'
      && this.task.childLeadId
      && this.task.childLeadId in this.pool
      ? this.pool[this.task.childLeadId]
      : null;
  }

  get lead(): Lead {
    if (this.useFather && this.father) {
      return this.father;
    }

    return this.childLead ?? this.$store.getters.currentLead;
  }

  get father(): Lead | null {
    if (!this.task || 'fatherLeadId' in this.task === false) return null;
    const fatherLeadId = this.task.relationship === 'child'
      ? this.task.fatherLeadId : this.task.LeadId;

    return fatherLeadId && fatherLeadId in this.pool
      ? this.pool[fatherLeadId]
      : null;
  }

  get selectedTaskUpdate(): { [key: string]: never } {
    return this.$store.getters.selectedTaskUpdate;
  }

  get selectedTaskUpdateAttachments(): { id: number; fieldname: string }[] {
    return this.selectedTaskUpdate?.attachments
      ? this.selectedTaskUpdate?.attachments as []
      : [];
  }

  get selectedTaskUpdateDeletedAttachments(): number[] {
    return this.selectedTaskUpdate?.deletedAttachments
      ? this.selectedTaskUpdate?.deletedAttachments as []
      : [];
  }

  // Ritorna l'elenco degli id dei nuovi allegati che stiamo caricando
  get selectedTaskUpdateAttachmentsIds(): number[] {
    if (this.name) {
      return this.selectedTaskUpdateAttachments
        .filter((a) => a.fieldname === this.name)
        .filter((a) => !this.selectedTaskUpdateDeletedAttachments.includes(a.id))
        .map((a) => a.id);
    }

    return this.selectedTaskUpdateAttachments
      .filter((a) => !this.selectedTaskUpdateDeletedAttachments.includes(a.id))
      .map((a) => a.id);
  }

  // L'elenco degli allegati memorizzati
  // (di un tipo specifico se richiesto tramite il parametro `name`)
  get attachments(): Attachment[] {
    if (!this.lead || !Array.isArray(this.lead.Attachments)) return [];

    if (this.name) {
      return this.lead.Attachments.filter(
        (attachment: Attachment) => attachment.fieldname === this.name,
      ) || [];
    }
    return this.lead.Attachments;
  }

  // Versione dell'elenco degli allegati della Lead da cui sono rimossi
  // quelli eliminati (non confermati)
  get filteredAttachments() {
    return this.attachments.filter((a: Attachment) => {
      // Nasconde gli allegati eliminati (non confermati)
      if (this.selectedTaskUpdateDeletedAttachments.includes(a.id)) return false;
      return true;
    });
  }

  // Ritorna il conteggio degli allegati, includendo anche i nuovi non ancora memorizzati
  get attachmentsCount(): number {
    return this.filteredAttachments.length + this.selectedTaskUpdateAttachmentsIds.length;
  }

  get noAttachments(): boolean { return this.attachmentsCount === 0; }

  get boxColor(): string {
    switch (this.boxStatus) {
      case 'todo':
        return 'grey';

      case 'done':
        return this.doneColor;

      case 'waiting':
        return 'orange';

      case 'na':
        return 'grey';

      default:
        break;
    }

    return '';
  }

  get boxDark(): boolean {
    // if (this.disabled) return false;

    switch (this.boxStatus) {
      case 'todo':
        return true;

      case 'done':
        return true;

      case 'waiting':
        return true;

      case 'na':
        return false;

      default:
        break;
    }

    return true;
  }

  get boxOutlined(): boolean {
    switch (this.boxStatus) {
      case 'todo':
      case 'done':
      case 'waiting':
        return false;

      case 'na':
        return true;

      default:
        break;
    }

    return false;
  }

  get boxText(): string {
    switch (this.boxStatus) {
      case 'todo':
        return 'Da fare';

      case 'done':
        return 'Fatto';

      case 'waiting':
        return 'In attesa';

      case 'na':
        return 'Non applicabile';

      default:
        break;
    }

    return 'Non disponibile';
  }

  async setBoxStatus(status: BoxStatusType) {
    if (this.boxStatus === status) {
      console.log(`TaskActivityBox: setBoxStatus() status = ${status} (non modificato, ignorato)`); // eslint-disable-line no-console
      return;
    }

    console.log('TaskActivityBox: setBoxStatus() status =', status); // eslint-disable-line no-console

    this.boxStatus = status;

    this.$emit('update', status);
  }
}
